/**
 * BLE Protocol Types for M-Scale Manager
 * Based on the M-Scale BLE Protocol Specification
 */

// Service UUID
export const M_SCALE_SERVICE_UUID = 'a0b1c2d3-e4f5-6789-abcd-ef0123456789'

// Characteristic UUIDs (Base UUID + offset)
const BASE_UUID = 'a0b1c2d3-e4f5-6789-abcd-ef01234567'

export const CHARACTERISTIC_UUIDS = {
  // Device Information
  DEVICE_NAME: `${BASE_UUID}01`,
  FIRMWARE_VERSION: `${BASE_UUID}02`,
  HARDWARE_CONFIG: `${BASE_UUID}03`,
  CUSTOM_SUFFIX: `${BASE_UUID}04`,

  // Weight Data
  ALL_WEIGHTS: `${BASE_UUID}10`,
  SINGLE_WEIGHT: `${BASE_UUID}11`,
  WEIGHT_UPDATE_RATE: `${BASE_UUID}12`,

  // Pairing Information
  PAIR_COUNT: `${BASE_UUID}20`,
  PAIR_DATA: `${BASE_UUID}21`,
  TARGET_RANGE: `${BASE_UUID}22`,
  GROUP_SIZE: `${BASE_UUID}23`,
  MIN_RATIO: `${BASE_UUID}24`,

  // System Control
  SYSTEM_COMMAND: `${BASE_UUID}30`,
  SYSTEM_STATUS: `${BASE_UUID}31`,
  TARE_COMMAND: `${BASE_UUID}32`,
  TIME_SYNC: `${BASE_UUID}33`,

  // Calibration
  CALIBRATION_COMMAND: `${BASE_UUID}40`,
  CALIBRATION_STATUS: `${BASE_UUID}41`,
  CALIBRATION_DATA: `${BASE_UUID}42`,

  // Spatial Mapping
  POSITION_MAP: `${BASE_UUID}50`,
  LED_MAP: `${BASE_UUID}51`,

  // LED Control (Diagnostics)
  LED_OVERRIDE: `${BASE_UUID}60`,
  LED_PATTERN: `${BASE_UUID}61`,

  // Device Link (Dual-Device Mode)
  DEVICE_LINK_STATUS: `${BASE_UUID}90`,
  DEVICE_LINK_CONTROL: `${BASE_UUID}91`,
  DEVICE_LINK_CONFIG: `${BASE_UUID}92`,
} as const

// Type definitions

export interface HardwareConfig {
  scaleCount: number
  gridRows: number
  gridCols: number
  activePositions: number[]
}

export interface ScaleWeight {
  position: number
  weight: number // in grams
}

export interface GroupInfo {
  positions: number[]  // Array of scale positions in this group (2-4)
  weights: number[]    // Individual weights for each position
  totalWeight: number
  isValid: boolean
  groupId: number      // Color/group identifier (0-3)
}

export type PairInfo = GroupInfo

export interface TargetRange {
  min: number // in grams
  max: number // in grams
}

export enum SystemMode {
  NORMAL = 0x00,
  CONFIG = 0x01,
  CALIBRATING = 0x02,
  ERROR = 0x03,
}

export enum SystemOperation {
  IDLE = 0x00,
  READING = 0x01,
  PAIRING = 0x02,
}

export interface SystemStatus {
  mode: SystemMode
  operation: SystemOperation
  uptime: number // in seconds
  errorCode: number
}

export enum SystemCommand {
  ENTER_CONFIG_MODE = 0x01,
  EXIT_CONFIG_MODE = 0x02,
  SAVE_CONFIGURATION = 0x03,
  RESET_TO_DEFAULTS = 0x04,
  RESTART_SYSTEM = 0x05,
}

export enum CalibrationCommand {
  CANCEL = 0x00,
  START_ZERO = 0x01,
  COMPLETE_ZERO = 0x02,
  START_SPAN = 0x03,
  COMPLETE_SPAN = 0x04,
}

export enum CalibrationStep {
  NOT_CALIBRATING = 0x00,
  AWAITING_ZERO = 0x01,
  READING_ZERO = 0x02,
  ZERO_COMPLETE = 0x03,
  AWAITING_SPAN = 0x04,
  READING_SPAN = 0x05,
  SPAN_COMPLETE = 0x06,
  CALIBRATION_COMPLETE = 0x07,
  CALIBRATION_ERROR = 0xFF,
}

export interface CalibrationStatus {
  position: number // 0xFF if not calibrating
  step: CalibrationStep
  currentRawReading: number
  progressPercent: number
}

export interface CalibrationData {
  position: number
  isActive: boolean
  isCalibrated: boolean
  zeroOffset: number
  calibrationFactor: number
  row: number
  col: number
}

// Device Link (Dual-Device Mode)

export enum DeviceRole {
  STANDALONE = 0,
  PRIMARY = 1,
  SECONDARY = 2,
}

export enum DeviceLinkCommand {
  CONNECT_TO_SECONDARY = 0x01,
  DISCONNECT = 0x02,
  QUERY_STATUS = 0x03,
}

export interface DeviceLinkStatus {
  linkActive: boolean
  role: DeviceRole
  linkedDeviceName: string
  linkedFirmwareVersion: number
}

export interface DeviceLinkConfig {
  relativePosition: 'right' | 'left' | 'below' | 'above'
  primaryRotation: 0 | 90 | 180 | 270
  secondaryRotation: 0 | 90 | 180 | 270
}

export interface PositionMapEntry {
  row: number
  col: number
  ledIndex: number
  hasLED: boolean
}

export enum LEDPattern {
  NORMAL = 0x00,
  ALL_WHITE = 0x01,
  RAINBOW = 0x02,
  CHASE = 0x03,
  GRID_TEST = 0x04,
}

export interface LEDOverride {
  position: number // 0xFF for all
  r: number
  g: number
  b: number
  duration: number // in 100ms units, 0=permanent
}

// BLE Connection State
export interface ConnectionState {
  isConnected: boolean
  deviceName: string | null
  device: BluetoothDevice | null
  server: BluetoothRemoteGATTServer | null
}

// Error types
export class BLEError extends Error {
  constructor(
    message: string,
    public code: BLEErrorCode,
    public originalError?: Error
  ) {
    super(message)
    this.name = 'BLEError'
  }
}

export enum BLEErrorCode {
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  NOT_ENABLED = 'NOT_ENABLED',
  USER_CANCELLED = 'USER_CANCELLED',
  DEVICE_NOT_FOUND = 'DEVICE_NOT_FOUND',
  CONNECTION_FAILED = 'CONNECTION_FAILED',
  SERVICE_NOT_FOUND = 'SERVICE_NOT_FOUND',
  CHARACTERISTIC_NOT_FOUND = 'CHARACTERISTIC_NOT_FOUND',
  READ_FAILED = 'READ_FAILED',
  WRITE_FAILED = 'WRITE_FAILED',
  NOTIFICATION_FAILED = 'NOTIFICATION_FAILED',
  DISCONNECTED = 'DISCONNECTED',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}
